import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { lazy, Suspense, useMemo } from "react";
import { Clock, User, Calendar, ChevronRight, Lock } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { ReadingProgress } from "@/components/site/article/ReadingProgress";
import { KeyTakeaways } from "@/components/site/article/KeyTakeaways";
import { TableOfContents } from "@/components/site/article/TableOfContents";
import { ArticleCTA } from "@/components/site/article/ArticleCTA";
import { NextStepsGrid } from "@/components/site/article/NextStepsGrid";
import { FAQ } from "@/components/site/article/FAQ";
import { RelatedArticles } from "@/components/site/article/RelatedArticles";
import { AcademyCTA } from "@/components/site/article/AcademyCTA";
import { TrackRecordCallout } from "@/components/site/article/TrackRecordCallout";
import { getArticleBySlug, getRelatedArticles, getNextArticle } from "@/lib/articles";

const SITE = "https://xauproacademy.com";

export const Route = createFileRoute("/education/$slug")({
  loader: async ({ params }) => {
    const article = getArticleBySlug(params.slug);
    if (!article) throw notFound();
    // "academy" articles are strictly paid — never expose them publicly.
    if (article.visibility === "academy") throw notFound();
    // Preload the article body so SSR HTML contains the content (good for SEO).
    await article.Content();
    return { slug: params.slug };
  },
  head: ({ params }) => {
    const article = params ? getArticleBySlug(params.slug) : undefined;
    if (!article) {
      return { meta: [{ title: "Article | XAU Pro Academy" }] };
    }
    const url = `${SITE}/education/${article.slug}`;
    const title = `${article.seoTitle ?? article.title} | XAU Pro Academy`;
    const absoluteImage = article.heroImage
      ? article.heroImage.startsWith("http")
        ? article.heroImage
        : `${SITE}${article.heroImage}`
      : undefined;
    const blogPosting = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: article.title,
      description: article.metaDescription,
      datePublished: article.publishedAt,
      dateModified: article.updatedAt,
      author: { "@type": "Organization", name: article.author },
      publisher: {
        "@type": "Organization",
        name: "XAU Pro Academy",
        url: SITE,
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      keywords: [article.focusKeyword, ...article.secondaryKeywords].join(", "),
      articleSection: article.category,
      ...(absoluteImage ? { image: absoluteImage } : {}),
    };
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE },
        { "@type": "ListItem", position: 2, name: "Education", item: `${SITE}/education` },
        { "@type": "ListItem", position: 3, name: article.title, item: url },
      ],
    };
    const isPreview = article.visibility === "preview";
    const faqSchema =
      !isPreview && article.faq.length
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: article.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }
        : null;
    return {
      meta: [
        { title },
        { name: "description", content: article.metaDescription },
        { name: "keywords", content: [article.focusKeyword, ...article.secondaryKeywords].join(", ") },
        { property: "og:title", content: article.title },
        { property: "og:description", content: article.metaDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "article:published_time", content: article.publishedAt },
        { property: "article:modified_time", content: article.updatedAt },
        { property: "article:author", content: article.author },
        { property: "article:section", content: article.category },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: article.title },
        { name: "twitter:description", content: article.metaDescription },
        ...(absoluteImage
          ? [
              { property: "og:image", content: absoluteImage },
              { property: "og:image:width", content: "1536" },
              { property: "og:image:height", content: "864" },
              { name: "twitter:image", content: absoluteImage },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(blogPosting) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
        ...(faqSchema
          ? [{ type: "application/ld+json", children: JSON.stringify(faqSchema) }]
          : []),
        ...(article.video
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "VideoObject",
                  name: article.video.name,
                  description: article.video.description,
                  thumbnailUrl: `https://i.ytimg.com/vi/${article.video.youtubeId}/hqdefault.jpg`,
                  uploadDate: article.video.uploadDate,
                  duration: article.video.duration,
                  embedUrl: `https://www.youtube.com/embed/${article.video.youtubeId}`,
                  contentUrl: `https://www.youtube.com/shorts/${article.video.youtubeId}`,
                  publisher: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
                }),
              },
            ]
          : []),
      ],
    };
  },
  component: ArticlePage,
  notFoundComponent: () => (
    <Layout>
      <div className="container mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-4xl font-bold">Article not found</h1>
        <p className="mt-3 text-muted-foreground">
          That lesson doesn't exist or has been moved.
        </p>
        <Link
          to="/education"
          className="mt-6 inline-flex rounded-md bg-gold px-5 py-2.5 text-sm font-semibold text-slate-deep"
        >
          Back to Education Hub
        </Link>
      </div>
    </Layout>
  ),
  errorComponent: ({ error }) => (
    <Layout>
      <div className="container mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Something went wrong</h1>
        <p className="mt-3 text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
      </div>
    </Layout>
  ),
});

function ArticlePage() {
  const { slug } = Route.useLoaderData();
  const article = getArticleBySlug(slug);
  const Content = useMemo(
    () => (article ? lazy(article.Content) : null),
    [article],
  );
  if (!article || !Content) return null;
  const related = getRelatedArticles(slug);
  const nextArticle = getNextArticle(slug);
  const dateLabel = new Date(article.publishedAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <Layout>
      <ReadingProgress />

      {/* Hero */}
      <section className="border-b border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-5xl px-4 py-12 md:py-16">
          {/* Breadcrumb */}
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-1.5 text-xs text-muted-foreground"
          >
            <Link to="/" className="hover:text-foreground">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/education" className="hover:text-foreground">Education</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-foreground/80 line-clamp-1">{article.title}</span>
          </nav>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="inline-block rounded-full bg-gold-muted px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gold">
              {article.category}
            </span>
            {article.visibility === "preview" && (
              <span className="inline-flex items-center gap-1 rounded-full border border-gold/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-gold">
                <Lock className="h-3 w-3" /> Lesson Preview
              </span>
            )}
          </div>
          <h1 className="mt-4 font-display text-3xl font-bold leading-tight md:text-5xl">
            {article.title}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-muted-foreground md:text-lg">
            {article.previewExcerpt ?? article.excerpt}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" /> {article.author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" /> {dateLabel}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> {article.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="container mx-auto max-w-6xl px-5 py-12 sm:px-6 md:py-16 lg:px-8">
        {article.visibility === "preview" ? (
          <div className="mx-auto max-w-2xl">
            <KeyTakeaways
              items={article.previewTakeaways ?? article.keyTakeaways.slice(0, 3)}
            />
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">
              {article.metaDescription}
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              This page is a public summary. The complete walkthrough, annotated
              XAU/USD charts, the full execution checklist, and the module's
              follow-up lessons, is delivered inside the XAU Pro Academy.
            </p>
            <AcademyCTA variant="preview" slug={article.slug} />
            <TrackRecordCallout />
            <RelatedArticles items={related} />
          </div>
        ) : (
          <>
            <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-14">
              <article className="min-w-0 max-w-2xl lg:max-w-none">
                <KeyTakeaways items={article.keyTakeaways} />
                <Suspense fallback={null}>
                  <Content />
                </Suspense>
                <ArticleCTA />
                <NextStepsGrid nextArticle={nextArticle} />
                <FAQ items={article.faq} />
                <AcademyCTA variant="feed" />
              </article>

              <aside className="hidden lg:block">
                <div className="sticky top-24">
                  <TableOfContents items={article.toc} />
                </div>
              </aside>
            </div>

            <RelatedArticles items={related} />
          </>
        )}
      </section>
    </Layout>
  );
}
